import { useMemo, useState } from 'react';
import type { CouponDefinition } from '@/lib/api-admin';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  DataTable,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  RowActions,
  RowActionButton,
  StatusBadge,
  Switch,
  toast,
  type DataTableColumn,
} from '@/components/ui';
import { Pencil } from 'lucide-react';
import { useHasRole } from '@/providers/AuthProvider';
import { friendlyMessage } from '@/lib/errors';
import { useCouponActiveMutation, useCouponDeleteMutation } from '../hooks/useCouponMutation';
import { CouponIssuedListDialog } from './CouponIssuedListDialog';

interface Props {
  items: ReadonlyArray<CouponDefinition>;
  onEdit?: (coupon: CouponDefinition) => void;
}

const formatDate = (iso?: string): string => (iso ? iso.slice(0, 10) : '—');

const isExpired = (validTo?: string): boolean => {
  if (!validTo) return false;
  return new Date(validTo).getTime() < Date.now();
};

const isScheduled = (validFrom?: string): boolean => {
  if (!validFrom) return false;
  return new Date(validFrom).getTime() > Date.now();
};

export const CouponList = ({ items, onEdit }: Props) => {
  const { t } = useTranslation('common');
  const deleteMutation = useCouponDeleteMutation();
  const activeMutation = useCouponActiveMutation();
  // 사용 여부 변경은 BE 가 SUPER_ADMIN·OPS_ADMIN 만 허용 — MD_MANAGER 는 보기만.
  const canToggleActive = useHasRole(['SUPER_ADMIN', 'OPS_ADMIN']);
  const [deleteTarget, setDeleteTarget] = useState<CouponDefinition | null>(null);
  // [coupon-audit] 발급 내역 다이얼로그 — GET /{couponId}/issued 소비(종전 미소비 endpoint).
  const [issuedTarget, setIssuedTarget] = useState<CouponDefinition | null>(null);

  const statusBadge = (row: CouponDefinition) => {
    if (row.active === false) {
      return <StatusBadge label={t('coupons.status.paused', '정지')} variant="danger" />;
    }
    if (isExpired(row.validTo)) {
      return <StatusBadge label={t('coupons.status.expired', '만료')} variant="neutral" />;
    }
    if (isScheduled(row.validFrom)) {
      return <StatusBadge label={t('coupons.status.scheduled', '예정')} variant="info" />;
    }
    return <StatusBadge label={t('coupons.status.active', '진행중')} variant="success" />;
  };

  const columns = useMemo<ReadonlyArray<DataTableColumn<CouponDefinition>>>(
    () => [
      {
        key: 'code',
        header: t('coupons.cols.code'),
        cell: (row) => (
          <span className="whitespace-nowrap font-mono text-[12px]" style={{ color: 'var(--content-muted)' }}>
            {row.code}
          </span>
        ),
        width: 170,
        headerAlign: 'center',
      },
      {
        key: 'title',
        header: t('coupons.cols.title'),
        cell: (row) => (
          <div className="flex items-center gap-1.5">
            <span
              className="block max-w-[360px] overflow-hidden text-ellipsis whitespace-nowrap font-medium"
              style={{ color: 'var(--content-strong)' }}
              title={row.title}
            >
              {row.title}
            </span>
            {row.badge ? (
              <span
                className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px]"
                style={{ background: 'var(--surface-1)', color: 'var(--content-muted)' }}
              >
                {row.badge}
              </span>
            ) : null}
          </div>
        ),
        width: 320,
        headerAlign: 'center',
      },
      {
        key: 'discount',
        header: t('coupons.cols.discount', '할인'),
        cell: (row) => (
          <span className="tabular-nums text-[12px]" style={{ color: 'var(--content-default)' }}>
            {row.discountType === 'FIXED'
              ? `${row.discountAmount.toLocaleString('ko-KR')}원`
              : `${row.discountAmount}%`}
          </span>
        ),
        width: 110,
        align: 'right',
        headerAlign: 'center',
      },
      {
        key: 'validity',
        header: t('coupons.cols.validity', '사용기간'),
        cell: (row) => (
          <span className="whitespace-nowrap tabular-nums text-[12px]" style={{ color: 'var(--content-muted)' }}>
            {formatDate(row.validFrom)} ~ {formatDate(row.validTo)}
          </span>
        ),
        width: 200,
        align: 'center',
      },
      {
        key: 'issued',
        header: t('coupons.cols.issued', '발급/최대'),
        cell: (row) => (
          <span className="tabular-nums text-[12px]" style={{ color: 'var(--content-default)' }}>
            {row.totalIssued} / {row.maxQuantity ?? '∞'}
          </span>
        ),
        width: 110,
        align: 'center',
      },
      {
        key: 'scope',
        header: t('coupons.cols.scope', '적용 범위'),
        cell: (row) => {
          const count = row.applicableProductIds?.length ?? 0;
          return count === 0 ? (
            <StatusBadge label={t('coupons.scopeAll', '전역')} variant="neutral" size="sm" />
          ) : (
            <StatusBadge
              label={t('coupons.scopeCount', '{{count}}개 상품', { count })}
              variant="info"
              size="sm"
            />
          );
        },
        width: 110,
        align: 'center',
      },
      {
        // QA-412 — 고도몰 쿠폰 "사용 여부"(사용/정지). 정지하면 발급·다운로드·사용이 막힌다.
        key: 'active',
        header: t('coupons.cols.active', '사용 여부'),
        cell: (row) => {
          const on = row.active !== false;
          return (
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-[12px]">
              <Switch
                checked={on}
                disabled={!canToggleActive || activeMutation.isPending}
                aria-label={`${row.title} ${t('coupons.cols.active', '사용 여부')}`}
                data-testid={`coupon-active-${row.id}`}
                onCheckedChange={(next) =>
                  activeMutation.mutate(
                    { couponId: row.id, active: next },
                    {
                      onSuccess: () =>
                        toast.success(
                          next
                            ? t('coupons.toast.activated', '쿠폰을 사용으로 바꿨습니다.')
                            : t('coupons.toast.paused', '쿠폰을 정지했습니다. 발급·다운로드·사용이 막힙니다.'),
                        ),
                      onError: (e) => toast.error(friendlyMessage(e, t('coupons.toast.activeFail', '사용 여부를 바꾸지 못했습니다.'))),
                    },
                  )
                }
              />
              <span style={{ color: on ? 'var(--content-default)' : 'var(--content-muted)' }}>
                {on ? t('coupons.active.on', '사용') : t('coupons.active.off', '정지')}
              </span>
            </span>
          );
        },
        width: 110,
        align: 'center',
      },
      {
        key: 'status',
        header: t('coupons.cols.status', '활성'),
        cell: (row) => (
          // QA-325: 좁은 칸에서 "진행중"이 두 줄로 꺾이지 않게.
          <span className="inline-block whitespace-nowrap">{statusBadge(row)}</span>
        ),
        width: 90,
        align: 'center',
      },
      {
        key: 'actions',
        header: '',
        align: 'right',
        width: 150,
        cell: (row) => (
          // 수정은 행의 [수정] 버튼 하나로 — ⋯ 메뉴에는 발급 내역·삭제만 둔다(QA-325, 같은 기능이 두 곳에 있었다).
          <div className="flex items-center justify-end gap-1.5 whitespace-nowrap">
            <RowActionButton
              onClick={() => onEdit?.(row)}
              title={t('coupons.edit', '수정')}
              className="whitespace-nowrap"
            >
              <Pencil size={12} aria-hidden="true" className="mr-1" />
              {t('coupons.edit', '수정')}
            </RowActionButton>
            <RowActions
              triggerLabel={`${row.title} ${t('coupons.actions.menu', '쿠폰 액션')}`}
              actions={[
                {
                  key: 'issued',
                  label: t('coupons.issuedTitle', '발급 내역'),
                  onClick: () => setIssuedTarget(row),
                },
                {
                  key: 'delete',
                  label: t('cms.delete'),
                  variant: 'danger',
                  disabled: deleteMutation.isPending,
                  onClick: () => setDeleteTarget(row),
                  separatorBefore: true,
                },
              ]}
            />
          </div>
        ),
      },
    ],
    [t, deleteMutation.isPending, activeMutation, canToggleActive],
  );

  return (
    <>
      <DataTable
        columns={[...columns]}
        rows={[...items]}
        rowKey={(row) => row.id}
        stickyHeader
        mobileCardCollapse
        emptyState={t('coupons.empty')}
        ariaLabel={t('coupons.title')}
        data-testid="coupon-table"
      />

      {issuedTarget ? (
        <CouponIssuedListDialog coupon={issuedTarget} onClose={() => setIssuedTarget(null)} />
      ) : null}

      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('coupons.deleteTitle')}</DialogTitle>
            <DialogDescription>
              {t('coupons.confirmDelete', { title: deleteTarget?.title ?? '' })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">
                {t('coupons.cancel')}
              </Button>
            </DialogClose>
            <Button
              variant="danger"
              size="sm"
              disabled={deleteMutation.isPending}
              onClick={() => {
                if (deleteTarget) {
                  deleteMutation.mutate(deleteTarget.id, {
                    onSuccess: () => setDeleteTarget(null),
                  });
                }
              }}
            >
              {t('cms.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
